import { theme, Tooltip } from "antd";
import { ExploreV1State } from "pages/api/deep-links/types";
import { VizIcon } from "src/components/VizIcon";
import { ContentViews } from "./lib/useContentView";
import { ExploreContext } from "./useExploreContext";
import { CSSProperties } from "react";
import { useApplicationContext } from "src/hooks/useApplication";

type IconProps = {
  inactive?: boolean;
  size?: number;
  style?: CSSProperties;
};

// Helper to check if a view is a table type view
const isTableView = (view: ContentViews) => {
  return [
    ContentViews.pivotTable,
    ContentViews.nestedTable,
    ContentViews.dataTable,
    ContentViews.unifiedTable,
  ].includes(view);
};

export function SavedVisualizations(props: {
  context: ExploreContext;
  vizs: ExploreV1State["viz"];
}) {
  const { token } = theme.useToken();
  const app = useApplicationContext();

  const chartDisabled = !(
    arrayOf(props.context.params.state.groupBys, { min: 1 }) &&
    arrayOf(props.context.params.state.aggregations, { max: 1 })
  );

  const options = [
    {
      title: "Bar",
      value: ContentViews.barChart,
      disabled: chartDisabled,
      config: {
        displayType: "bars",
        vertical: false,
      },
      icon: (props: IconProps) => (
        <VizIcon {...props} value={ContentViews.barChart} />
      ),
    },
    {
      title: "Vertical",
      value: ContentViews.barChart,
      disabled: chartDisabled,
      config: {
        displayType: "bars",
        vertical: true,
      },
      icon: (props: IconProps) => (
        <VizIcon {...props} value={ContentViews.barChart} />
      ),
    },
    {
      title: "Line",
      value: ContentViews.barChart,
      disabled: chartDisabled,
      config: {
        displayType: "lines",
        curveType: "monotone",
      },
      icon: (props: IconProps) => (
        <VizIcon {...props} value={ContentViews.barChart} />
      ),
    },
    {
      title: "Area",
      value: ContentViews.barChart,
      disabled: chartDisabled,
      config: {
        displayType: "area",
        curveType: "monotone",
      },
      icon: (props: IconProps) => (
        <VizIcon {...props} value={ContentViews.barChart} />
      ),
    },
    {
      title: "Heatmap",
      value: ContentViews.heatMap,
      disabled: !(
        arrayOf(props.context.params.state.groupBys, { min: 1, max: 2 }) &&
        arrayOf(props.context.params.state.aggregations, { max: 1 })
      ),
      icon: (props: IconProps) => (
        <VizIcon {...props} value={ContentViews.heatMap} />
      ),
    },
    {
      title: "Metric",
      value: ContentViews.metric,
      disabled: !arrayOf(props.context.params.state.groupBys, {
        min: 1,
        max: 1,
      }),
      icon: (props: IconProps) => (
        <VizIcon {...props} value={ContentViews.metric} />
      ),
    },
    {
      title: "Time",
      value: ContentViews.overTime,
      disabled: false,
      icon: (props: IconProps) => (
        <VizIcon {...props} value={ContentViews.overTime} />
      ),
    },
    {
      title: "Treemap",
      value: ContentViews.treeMap,
      disabled: !(
        arrayOf(props.context.params.state.groupBys, { min: 1, max: 2 }) &&
        arrayOf(props.context.params.state.aggregations, { max: 1 })
      ),
      icon: (props: IconProps) => (
        <VizIcon {...props} value={ContentViews.treeMap} />
      ),
    },
    {
      title: "Table",
      value: ContentViews.unifiedTable,
      disabled: false,
      icon: (props: IconProps) => (
        <VizIcon {...props} value={ContentViews.unifiedTable} />
      ),
    },
  ];

  const isSelected = (option: (typeof options)[0]) => {
    // If this is the SmartTable option, it should appear selected for any table view
    if (option.value === ContentViews.unifiedTable) {
      return isTableView(props.context.view.value);
    }

    // For other visualizations, use the original logic
    return (
      props.context.view.value === option.value &&
      (!option.config ||
        Object.entries(option.config).every(
          ([key, value]) => props.context.view.config[key] === value
        ))
    );
  };

  return (
    <div className="visualization-menu">
      <div className="visualization-grid">
        {options.map((option) => (
          <Tooltip
            key={`${option.value}-${option.config?.displayType || ""}-${
              option.config?.vertical || ""
            }`}
            title={
              option.disabled
                ? "Unable to visualize this chart with the current query state"
                : option.title
            }
          >
            <button
              key={`${option.value}-${option.config?.displayType || ""}-${
                option.config?.vertical || ""
              }`}
              onClick={() => {
                if (!option.disabled) {
                  // Always reset the view when clicking Smart Table
                  if (option.value === ContentViews.unifiedTable) {
                    props.context.view.set(option.value);
                    props.context.view.setConfig({
                      layout: "flat", // Reset to flat layout by default
                    });
                    return;
                  }
                  props.context.view.set(option.value);
                  if (option.config) {
                    props.context.view.setConfig(option.config);
                  }
                }
              }}
              className={`viz-option ${isSelected(option) ? "selected" : ""} ${
                option.disabled ? "disabled" : ""
              }`}
              disabled={option.disabled}
            >
              <VizIcon
                inactive={option.disabled}
                size={24}
                value={option.value}
                config={option.config}
              />
              <div className="viz-option-title">{option.title}</div>
            </button>
          </Tooltip>
        ))}
      </div>
    </div>
  );
}

function arrayOf(
  arr: any[] | undefined,
  { min, max }: { min?: number; max?: number }
) {
  return arr && (!min || arr.length >= min) && (!max || arr.length <= max);
}
